(ns dashboard.views.analysis.common
  (:require ["react" :as react]
            [clojure.set :as set]
            [clojure.string :as str]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.analysis.modals :as analysis.modals]
            [dashboard.entity.analysis.profile :as analysis.profile]
            [dashboard.utils :as utils]
            [dashboard.views.analysis.structured-analysis.view-structured-analysis-modal :as-alias view-structured-analysis]
            [re-frame.core :as rf]))

;; TODO: Consider different namespaces for sections, to help in putting more stuff into correct "location"


(defn diff-report [applied-analyses-outdated]
  (str "Applied analysis is outdated for this structured analysis. Please update the applied analysis, if possible."
       "\n\nThere are changes in fields:\n\n"
       (->> applied-analyses-outdated
            (map
             (fn [[k v]]
               (str
                (name k)
                ": "
                (if (vector? v)
                  (str "added/changed "
                       (let [added (remove nil? v)]
                         (case k
                           :states (->> added
                                        (map #(str "'" (second %) "'"))
                                        (str/join ", "))
                           :categories (->> added
                                            (map #(str "'" (:name %) "'"))
                                            (str/join ", "))
                           added)))
                  (str "change to: "
                       (case k
                         :state-dependencies
                         "New state dependency added"
                         v))))))
        (str/join "\n"))))

;; Used only for structured analysis modal
(defn find-family-ids
  "Find the descendant ids of root, including root itself"
  [construct-id all-structured-analyses]
  (loop [to-visit #{construct-id}
         visited #{}]
    (if (empty? to-visit)
      visited
      (let [current (first to-visit)
            remaining (disj to-visit current)
            direct-relatives (filter #(= current (:parent-id %)) all-structured-analyses)
            children (set (map :construct-id direct-relatives))
            new-to-visit (set/union remaining (set/difference children visited))
            new-visited (conj visited current)]
        (recur new-to-visit new-visited)))))

(defn get-state-by-id [states id]
  (->> states
       (filter (fn [state]
                 (= (first state) id)))
       first))

(defn search-query-filter [search-query {:keys [applied-analyses] :as structured-analysis}]
  (utils/match-search-query
   search-query
   (fn [query-word]
     (let [latest-applied-analysis (first applied-analyses)]
       (or (utils/str-includes-icase? (:construct structured-analysis) query-word)
           (utils/str-includes-icase? (-> structured-analysis
                                          :construct-id
                                          analysis.profile/format-analysis-id)
                                      query-word)
           (when latest-applied-analysis
             (let [{:keys [states]} structured-analysis
                   state-id->state-name (into {} states)
                   selected-state-name (state-id->state-name (:state-id latest-applied-analysis) "")]
               (utils/str-includes-icase? selected-state-name query-word)))
           (utils/str-includes-icase? (:evidence-search-text latest-applied-analysis)
                                      query-word)
           (some #(utils/str-includes-icase? (:name %) query-word)
                 (:categories structured-analysis))
           (some #(utils/str-includes-icase? % query-word)
                 (:relates-to structured-analysis))
           (some #(utils/str-includes-icase? % query-word)
                 (:excludes structured-analysis)))))))

(defn is-not-yet-evaluated?
  [text]
  (utils/str-includes-icase? text "Not Yet Evaluated"))

(defn in-initial-state? [{:keys [state-id structured-analysis] :as _applied-analysis}]
  (let [{:keys [states]} structured-analysis
        state-id->state-name (into {} states)]
    (is-not-yet-evaluated? (state-id->state-name state-id))))

(defn category-sort-key [{:keys [construct-id categories]}]
  ;; Break ties by ordering with construct-id
  (conj (mapv (comp #(.toLowerCase %) :name) categories) construct-id))

(defn state-sort-key [{:keys [applied-analyses]}]
  (let [{:keys [state-id structured-analysis]} (first applied-analyses)
        state-id->state-name (into {} (:states structured-analysis))]
    (state-id->state-name state-id)))

(def construct-sort-key (comp #(.toLowerCase %) :construct))

;; Common components

(defn applied-analysis-column
  [applied-analyses questionnaire-id question answer-id overflow-disabled?]
  (let [[unlinking-state set-unlinking-state] (react/useState {})
        [linking? set-linking] (react/useState false)
        [overflow-hidden? set-overflow-hidden] (react/useState (if overflow-disabled?
                                                                 false
                                                                 true))
        overflow-limit 10
        show-partial (and overflow-hidden?
                          (> (count applied-analyses) overflow-limit))]
    [:div (when (and (not overflow-disabled?)
                     (> (count applied-analyses) overflow-limit))
            {:on-click #(when-not overflow-disabled?
                          (set-overflow-hidden (not overflow-hidden?)))
             :style {:cursor (if overflow-hidden?
                               "s-resize"
                               "n-resize")}
             :title (if show-partial
                      "Click to show all applied analyses linked to the selected answer"
                      "Click to hide rest of applied analyses linked to the selected answer")})
     [:table {:style {:width "100%"
                      :border-collapse "collapse"}}
      [:tbody
       (let [rows (if show-partial
                    (take overflow-limit applied-analyses)
                    applied-analyses)]
         (doall (for [[idx {:keys [version-id
                                   next-version-id
                                   construct-id
                                   state-id
                                   structured-analysis]
                            :as applied-analysis}] (map-indexed vector rows)
                      :let [{:keys [states construct]} structured-analysis
                            id->state (into {} states)
                            analysis-unlinking? (get unlinking-state version-id)]]
                  [:tr {:key (str version-id)
                        :style {:background-color "unset"}}
                   [:td.p-0
                    {:style {:border "none"}}
                    [:a.is-flex
                     {:class (when next-version-id
                               "has-background-warning-light")
                      :on-click (fn [e]
                                  (.stopPropagation e)
                                  (analysis.modals/show-editor-modal construct-id
                                                                     :view
                                                                     applied-analysis))}
                     (analysis.profile/format-analysis-id construct-id)
                     [:span.ml-2 construct]]]
                   [:td.p-0
                    {:style {:border "none"}}
                    [analysis.profile/state-tag
                     {:class ["is-small" "ml-2"]}
                     (id->state state-id)]]
                   [:td.pt-1
                    {:style {:border "none"}}
                    [:div.is-flex
                     [:button.button.is-rounded.is-circular.is-small.is-danger
                      {:title "Unlink the applied analysis from the answer to the question"
                       :on-click (fn [e]
                                   (.stopPropagation e)
                                   (analysis.db/unlink-applied-analysis
                                    questionnaire-id
                                    (:id question)
                                    answer-id
                                    construct-id
                                    version-id
                                    (fn [linking?]
                                      (set-unlinking-state
                                       (assoc unlinking-state version-id linking?)))))}
                      (if analysis-unlinking?
                        [:div.loader]
                        [:span.icon>i.fas.fa-unlink])]]]
                   (when (and show-partial
                              (= overflow-limit (inc idx)))
                     [:td.p-0
                      {:style {:border "none"}}
                      [:span
                       (str (count (drop overflow-limit applied-analyses))
                            " more...")]])])))]]
     [:button.button.is-rounded.is-secondary.is-small
      {:on-click #(let [current-applied-analysis-version-ids (set (map :version-id applied-analyses))]
                    (analysis.modals/select-analyses-for-question
                     current-applied-analysis-version-ids
                     (fn on-submit [checked-analyses]
                       (analysis.db/link-analysis
                        (fn [_applied-analysis-version-ids state]
                          (set-linking state))
                        questionnaire-id
                        (:id question)
                        (map (fn [[construct-id applied-analysis-version-id]]
                               {:construct-id construct-id
                                :version-id applied-analysis-version-id})
                             checked-analyses)))
                     question))
       :title "Browse applied analyses"}
      (if linking?
        [:div.loader]
        [:span.icon>i.fas.fa-folder-tree])]]))

(defn IdColumn [{:keys [construct-id construct]
                 :tree/keys [ancestor-nodes show-ancestors]}]
  [:div
   (if show-ancestors
     [:div.is-flex
      [:b
       {:title construct}
       (analysis.profile/format-analysis-id construct-id)]
      (when (seq ancestor-nodes)
        (->> ancestor-nodes
             (map-indexed (fn [idx {:keys [construct-id construct]}]
                            (let [formatted-construct-id (analysis.profile/format-analysis-id construct-id)]
                              (if (zero? idx)
                                [:span.mr-2
                                 {:title construct}
                                 formatted-construct-id]
                                [:span.mx-2
                                 {:title construct}
                                 formatted-construct-id]))))
             (interpose [:span "<"])
             (into [:div.is-flex
                    [:span.ml-2.mr-2 "<"]])))]
     (analysis.profile/format-analysis-id construct-id))])

(defn CategoryColumn [{:keys [categories]}]
  [:div.columns
   (map (fn [{:keys [id name]}]
          [:span.tag.ml-1.is-category {:key (str "category-" id)}
           name])
        categories)])

(defn StateColumn [{:keys [applied-analyses]}]
  (let [{:keys [state-id structured-analysis] :as _latest-applied-analysis} (first applied-analyses)
        state-id->state-name (into {} (:states structured-analysis))]
    (when-let [state-name (state-id->state-name state-id)]
      [analysis.profile/state-tag {} state-name])))

(defn ConstructColumn [{:keys [construct-id construct parent-id applied-analyses]
                        :tree/keys [children depth show-ancestors]
                        :view/keys [features-enabled]}
                       hidden-roots
                       set-hidden-roots]
  (let [structured-analysis-creation-enabled? (contains? features-enabled :structured-analysis-creation)
        applied-analysis-creation-enabled? (contains? features-enabled :applied-analysis-creation)
        on-click #(cond (and applied-analysis-creation-enabled?
                             (seq applied-analyses))
                        (analysis.modals/show-editor-modal construct-id :view nil)
                        (and applied-analysis-creation-enabled?
                             (empty? applied-analyses))
                        (analysis.modals/show-editor-modal construct-id :edit nil)
                        structured-analysis-creation-enabled?
                        (rf/dispatch [::view-structured-analysis/show-view-modal construct-id]))]
    (fn [_ _ _]
      [:div.is-flex
       (when-not show-ancestors
         {:style {:padding-left (str (* depth 24) "px")}})
       (if (seq children)
         [:div {:title "Toggle child visibility"
                :style {:cursor "pointer"}
                :on-click #(set-hidden-roots (if (contains? hidden-roots construct-id)
                                               (disj hidden-roots construct-id)
                                               (conj hidden-roots construct-id)))}
          (cond
            (contains? hidden-roots construct-id)
            [:span.icon>i.fas.fa-plus]

            (and (seq children)
                 (not show-ancestors))
            [:span.icon>i.fas.fa-chevron-right])]
         ;; To make the leaf node indented
         (when-not show-ancestors
           [:div {:style {:width "26px"}}]))
       [:div
        {:title (str "View: " construct)
         :on-click on-click
         :style {:width "100%"
                 :cursor "pointer"}
         :class (when-not parent-id
                  "has-text-weight-bold")}
        construct]])))

(def construct-id-column
  {:label "ID"
   :id :construct-id
   :sort-key :construct-id
   :render IdColumn})
